import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { MSG } from "@vocifuchs/shared";
import { api, ApiError } from "../lib/api.js";
import { auth } from "../copy/auth.js";
import { common } from "../copy/common.js";
import { errors } from "../copy/errors.js";
import { Banner, Button, Card, Field, inputClass } from "../components/ui.js";
import { OneTimeCodeField } from "../components/OneTimeCodeField.js";
import { QrCode } from "../components/QrCode.js";
import { joinLink } from "../lib/join.js";
import { useSession } from "../session.js";

interface ClassDetail {
  id: string;
  name: string;
  role: "member" | "admin";
  join_code?: string;
}

interface Member {
  user_id: string;
  display_name: string;
  role: "member" | "admin";
  joined_at: number;
}

export function ClassScreen() {
  const { user, membership } = useSession();
  if (!user) return null;
  if (membership?.role === "admin") return <AdminClass classId={membership.id} />;
  if (!membership) return <CreateClassForm needInvite={user.role === "learner"} />;
  return <Navigate to="/" replace />;
}

function CreateClassForm({ needInvite }: { needInvite: boolean }) {
  const { refresh } = useSession();
  const [name, setName] = useState("");
  const [invite, setInvite] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api("/api/classes", {
        method: "POST",
        body: JSON.stringify({
          name,
          ...(invite.trim() ? { adult_invite: invite.trim() } : {}),
        }),
      });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errors.failed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md space-y-6">
      <Link to="/me" className="inline-flex font-semibold text-fox-ink underline-offset-4 hover:underline">
        {common.backToMe}
      </Link>
      <h1 className="text-3xl sm:text-4xl">{auth.class.createTitle}</h1>
      <p className="text-ink-soft">{auth.class.createIntro}</p>
      <form className="space-y-4" onSubmit={(e) => void submit(e)}>
        {error ? <Banner>{error}</Banner> : null}
        <Field label={auth.class.className} hint={auth.class.classNameHint}>
          <input
            className={inputClass()}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={40}
            placeholder="5b Voci"
          />
        </Field>
        {needInvite ? (
          <OneTimeCodeField
            label={auth.class.inviteCode}
            hint={auth.class.inviteHint}
            value={invite}
            onChange={setInvite}
          />
        ) : null}
        <Button type="submit" disabled={busy}>
          {busy ? common.loading : auth.class.createSubmit}
        </Button>
      </form>
      <CoLeadForm />
    </div>
  );
}

/** Redeem a co-lead code (issue #41): lead an existing class instead of a new one. */
function CoLeadForm() {
  const { refresh } = useSession();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api("/api/classes/lead", {
        method: "POST",
        body: JSON.stringify({ invite_code: code.trim() }),
      });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errors.failed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-3 border-t border-line pt-6">
      <h2 className="text-xl">{auth.class.coLeadTitle}</h2>
      <p className="text-ink-soft">{auth.class.coLeadIntro}</p>
      <form className="space-y-3" onSubmit={(e) => void submit(e)}>
        {error ? <Banner>{error}</Banner> : null}
        <OneTimeCodeField label={auth.class.inviteCode} value={code} onChange={setCode} />
        <Button type="submit" variant="ghost" disabled={busy}>
          {busy ? common.loading : auth.class.coLeadSubmit}
        </Button>
      </form>
    </section>
  );
}

function AdminClass({ classId }: { classId: string }) {
  const navigate = useNavigate();
  const { refresh } = useSession();
  const [detail, setDetail] = useState<ClassDetail | null>(null);
  const [members, setMembers] = useState<Member[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shown, setShown] = useState<{ kind: "reset" | "invite"; label: string; code: string } | null>(
    null,
  );

  const load = async () => {
    const [cls, list] = await Promise.all([
      api<ClassDetail>(`/api/classes/${classId}`),
      api<{ members: Member[] }>(`/api/classes/${classId}/members`),
    ]);
    setDetail(cls);
    setMembers(list.members);
  };

  useEffect(() => {
    void load().catch((err: unknown) => {
      setError(err instanceof ApiError ? err.message : errors.failed);
    });
  }, [classId]);

  const rotate = async () => {
    setBusy(true);
    setError(null);
    try {
      const next = await api<{ join_code: string }>(`/api/classes/${classId}/code`, { method: "POST" });
      setDetail((current) => (current ? { ...current, join_code: next.join_code } : current));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errors.failed);
    } finally {
      setBusy(false);
    }
  };

  const invite = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await api<{ invite_code: string }>("/api/adult-invites", { method: "POST" });
      setShown({ kind: "invite", label: auth.class.inviteForTeacher, code: data.invite_code });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errors.failed);
    } finally {
      setBusy(false);
    }
  };

  const inviteLead = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await api<{ invite_code: string }>(`/api/classes/${classId}/leader-invites`, {
        method: "POST",
      });
      setShown({ kind: "invite", label: auth.class.leadInviteLabel, code: data.invite_code });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errors.failed);
    } finally {
      setBusy(false);
    }
  };

  // Leaving or closing, per issue #41. The server decides again, atomically;
  // this only picks the right button and sentence.
  const leaveAsLead = async (closing: boolean) => {
    if (!detail) return;
    const question = closing ? auth.class.closeClassConfirm(detail.name) : auth.class.leaveAsLeadConfirm(detail.name);
    if (!window.confirm(question)) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/api/classes/${classId}/members/me`, { method: "DELETE" });
      await refresh();
      navigate("/me", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errors.failed);
    } finally {
      setBusy(false);
    }
  };

  const resetPin = async (member: Member) => {
    setBusy(true);
    setError(null);
    try {
      const data = await api<{ reset_code: string }>(
        `/api/classes/${classId}/members/${member.user_id}/reset`,
        { method: "POST" },
      );
      setShown({
        kind: "reset",
        label: auth.class.resetPinLabel(member.display_name),
        code: data.reset_code,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errors.failed);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (member: Member) => {
    const ok = window.confirm(auth.class.removeConfirm(member.display_name));
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/api/classes/${classId}/members/${member.user_id}`, { method: "DELETE" });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errors.failed);
    } finally {
      setBusy(false);
    }
  };

  if (!detail) {
    return error ? <Banner>{error}</Banner> : <p className="text-ink-soft">{common.loading}</p>;
  }

  const leads = (members ?? []).filter((member) => member.role === "admin").length;

  // Only the lead gets the code from the API, so only the lead sees a link.
  const link = detail.join_code ? joinLink(detail.join_code) : null;

  return (
    <div className="space-y-8">
      <Link to="/me" className="inline-flex font-semibold text-fox-ink underline-offset-4 hover:underline">
        {common.backToMe}
      </Link>
      <header>
        <h1 className="text-3xl sm:text-4xl">{detail.name}</h1>
      </header>
      {error ? <Banner>{error}</Banner> : null}

      <Card className="space-y-4 p-5 sm:p-7">
        <p className="text-sm font-semibold text-ink-soft">{auth.class.classCode}</p>
        <p className="rounded-3xl border-2 border-dashed border-fox/40 bg-paper-2 px-4 py-6 text-center font-mono text-3xl tracking-[0.22em] sm:text-4xl">
          {detail.join_code}
        </p>
        {link ? (
          <>
        <div className="space-y-2">
          <p className="text-sm font-semibold text-ink-soft">{auth.class.shareLink}</p>
          {/* Selectable, for when copying to the clipboard is not allowed. */}
          <p className="rounded-2xl bg-paper-2 px-4 py-3 font-mono text-sm break-all select-all">
            {link}
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              variant="ghost"
              onClick={() => {
                void navigator.clipboard
                  ?.writeText(link)
                  .then(() => setCopied(true))
                  .catch(() => setCopied(false));
              }}
            >
              <span aria-live="polite">{copied ? auth.class.linkCopied : auth.class.copyLink}</span>
            </Button>
            <Button variant="ghost" onClick={() => setQrOpen((open) => !open)}>
              {qrOpen ? auth.class.hideQr : auth.class.showQr}
            </Button>
          </div>
        </div>
        {qrOpen ? (
          <div className="space-y-2 text-center">
            {/* Big enough to project or print; white ground in dark mode too. */}
            <QrCode
              text={link}
              label={auth.class.qrAria(detail.name)}
              className="mx-auto w-full max-w-sm rounded-2xl"
            />
            <p className="text-sm text-ink-soft">{auth.class.qrHint}</p>
          </div>
        ) : null}
          </>
        ) : null}
        <div className="space-y-1 sm:max-w-xs">
          <Button
            variant="ghost"
            onClick={() => {
              setCopied(false);
              void rotate();
            }}
            disabled={busy}
          >
            {auth.class.rotateCode}
          </Button>
          <p className="text-sm text-ink-soft">{auth.class.rotateHint}</p>
        </div>
      </Card>

      {shown ? (
        <Card className="space-y-3 p-5 sm:p-7">
          <p className="font-semibold">{shown.label}</p>
          <p className="rounded-3xl border-2 border-dashed border-line bg-paper-2 px-4 py-6 text-center font-mono text-2xl tracking-[0.18em] break-all">
            {shown.code}
          </p>
          <p className="text-sm text-ink-soft">{auth.class.showOnce}</p>
          <div className="sm:max-w-xs">
            <Button variant="ghost" onClick={() => setShown(null)}>
              {common.done}
            </Button>
          </div>
        </Card>
      ) : null}

      <section className="space-y-3">
        <h2 className="text-xl sm:text-2xl">{auth.class.members}</h2>
        <ul className="space-y-2">
          {(members ?? []).map((member) => (
            <Card as="li" key={member.user_id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="font-semibold break-words">{member.display_name}</p>
              </div>
              {member.role === "member" ? (
                <div className="flex flex-col gap-2 sm:w-48">
                  <Button variant="ghost" onClick={() => void resetPin(member)} disabled={busy}>
                    {auth.class.resetPin}
                  </Button>
                  <button
                    type="button"
                    className="min-h-12 rounded-2xl px-4 text-sm font-semibold text-rose underline-offset-4 hover:underline"
                    onClick={() => void remove(member)}
                    disabled={busy}
                  >
                    {auth.class.remove}
                  </button>
                </div>
              ) : (
                <p className="text-sm text-ink-soft">{auth.class.leadership}</p>
              )}
            </Card>
          ))}
        </ul>
      </section>

      <section className="space-y-3 sm:max-w-sm">
        <h2 className="text-xl">{auth.class.leadTitle}</h2>
        <p className="text-ink-soft">{auth.class.leadIntro}</p>
        <Button variant="ghost" onClick={() => void inviteLead()} disabled={busy}>
          {auth.class.leadInvite}
        </Button>
        {members === null ? null : members.length === 1 ? (
          <button
            type="button"
            className="min-h-12 w-full rounded-2xl px-4 text-sm font-semibold text-rose underline-offset-4 hover:underline disabled:opacity-50"
            onClick={() => void leaveAsLead(true)}
            disabled={busy}
          >
            {auth.class.closeClass}
          </button>
        ) : leads > 1 ? (
          <Button variant="ghost" onClick={() => void leaveAsLead(false)} disabled={busy}>
            {auth.class.leaveAsLead}
          </Button>
        ) : (
          // Say why, not only that it does not work (issue #41).
          <p className="text-sm text-ink-soft">{MSG.lastLeader}</p>
        )}
      </section>

      <section className="space-y-3 sm:max-w-sm">
        <h2 className="text-xl">{auth.class.anotherClass}</h2>
        <p className="text-ink-soft">{auth.class.anotherClassHint}</p>
        <Button variant="ghost" onClick={() => void invite()} disabled={busy}>
          {auth.class.createInvite}
        </Button>
      </section>
    </div>
  );
}
